/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import lightUrl from "../graphics/backgrounds/light.webp?url";
import darkUrl from "../graphics/backgrounds/dark.webp?url";
import tealUrl from "../graphics/backgrounds/teal.webp?url";

/**
 * Подмена фона картинкой.
 *
 * CTO 14.09.2026 — в ответ на «как выбрать фон для своей камеры?»: «бери в
 * работу». До этого в звонке было только размытие: комнату оно прячет, но
 * поставить вместо неё картинку было нечем.
 *
 * Считать ничего нового не нужно: разделение человека и фона уже идёт
 * (MediaPipe selfie segmenter), и библиотека умеет рисовать за человеком
 * картинку вместо размытой копии кадра. Стоимость кадра та же.
 */

/** Встроенная картинка фона. */
export interface BuiltInBackground {
  /** Как она зовётся в настройках и в хранилище. */
  id: string;
  /** Ключ перевода для подписи. */
  labelKey: string;
  /** Адрес картинки в сборке. */
  url: string;
}

/**
 * Готовые фоны.
 *
 * Это спокойные заливки, а не фотографии: снимков офиса у нас нет, а чужие
 * брать нельзя. Они нужны, чтобы у человека был выбор сразу после обновления,
 * не дожидаясь, пока дизайн нарисует свои. Своя картинка при этом доступна
 * всегда — {@link CUSTOM_BACKGROUND}.
 */
export const builtInBackgrounds: readonly BuiltInBackground[] = [
  { id: "light", labelKey: "settings.background_image_light", url: lightUrl },
  { id: "dark", labelKey: "settings.background_image_dark", url: darkUrl },
  { id: "teal", labelKey: "settings.background_image_teal", url: tealUrl },
];

/** Значение настройки, означающее «моя собственная картинка». */
export const CUSTOM_BACKGROUND = "custom";

/** Значение настройки, означающее «картинку не ставим». */
export const NO_BACKGROUND = "";

/** Адрес встроенной картинки по её имени; `undefined` — такой нет. */
export function builtInBackgroundUrl(id: string): string | undefined {
  return builtInBackgrounds.find((b) => b.id === id)?.url;
}
